import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { toast } from "sonner";
import { SelectedFundAtom } from "@/store/FundStateAtom";
import { getValidationRulesNew, createValidationRuleNew, getSupportedRuleTypes, validateRules, ValidationRuleDto } from "@/api/Validations";
import { RuleTemplates } from "@/types/ValidationRule";

// Performance and data quality logging
const logStateComplexity = (stateSnapshot: any) => {
  console.log('[VALIDATION_RULES_ANALYSIS] State Complexity:', {
    validationRulesCount: stateSnapshot.validationRules?.length || 0,
    editingRulesCount: stateSnapshot.editingRules?.length || 0,
    draftRulesCount: stateSnapshot.draftRules?.length || 0,
    editingStatesCount: Object.keys(stateSnapshot.editingStates || {}).length,
    timestamp: new Date().toISOString()
  });
};

const logDataCorruption = (rule: ValidationRuleDto, fieldName: string, originalValue: any, cleanedValue: any) => {
  console.warn('[DATA_CORRUPTION_DETECTED]', {
    ruleId: rule.validationRuleId,
    ruleName: rule.ruleName,
    fieldName,
    originalValue,
    cleanedValue,
    corruptionType: originalValue?.includes?.('object Object') ? 'OBJECT_SERIALIZATION' : 
                   originalValue?.includes?.('[[[') ? 'NESTED_BRACKETS' : 'OTHER',
    timestamp: new Date().toISOString()
  });
};

export const useFundValidationRulesController = () => {
  const selectedFund = useAtomValue(SelectedFundAtom);
  const [validationRules, setValidationRules] = useState<ValidationRuleDto[]>([]);
  const [editingRules, setEditingRules] = useState<ValidationRuleDto[]>([]);
  const [draftRules, setDraftRules] = useState<ValidationRuleDto[]>([]);
  const [editingStates, setEditingStates] = useState<{ [key: string]: boolean }>({});
  const [supportedRuleTypes, setSupportedRuleTypes] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Log component renders and state complexity
  useEffect(() => {
    console.log('[VALIDATION_RULES_CONTROLLER] Component render, selected fund:', selectedFund?.fundId);
    logStateComplexity({
      validationRules,
      editingRules,
      draftRules,
      editingStates
    });
  }, [validationRules, editingRules, draftRules, editingStates, selectedFund]);

  const fetchSupportedRuleTypes = async () => {
    try {
      const types = await getSupportedRuleTypes();
      const supportedTypes = types
      
      setSupportedRuleTypes(supportedTypes);
    } catch (err) {
      console.error("Error fetching supported rule types:", err);
      // Fallback to include the new rule type
      setSupportedRuleTypes([
        "nav-threshold-percent-change",
        "holdings-consistency",
        "transaction-reconciliation",
        "unexplained-cash-movement"
      ]);
    }
  };

  const fetchValidationRules = async () => {
    if (!selectedFund?.fundId) {
      setValidationRules([]);
      setEditingRules([]);
      setDraftRules([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const rules = await getValidationRulesNew(selectedFund.fundId);
      setValidationRules(rules);
      setEditingRules([...rules]);
    } catch (err) {
      console.error("Error fetching validation rules:", err);
      setError("Failed to load validation rules. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const refreshRules = () => {
    fetchValidationRules();
  };

  const startEditing = (ruleId: number) => {
    setEditingStates(prev => ({ ...prev, [ruleId]: true }));
  };

  const cancelEditing = (ruleId: number) => {
    setEditingStates(prev => ({ ...prev, [ruleId]: false }));
    
    // Check if it's a draft rule
    const isDraft = draftRules.some(rule => rule.validationRuleId === ruleId);
    if (isDraft) {
      // Remove draft rule
      setDraftRules(prev => prev.filter(rule => rule.validationRuleId !== ruleId));
      setEditingRules(prev => prev.filter(rule => rule.validationRuleId !== ruleId));
    } else {
      // Reset the editing rule to original value
      const originalRule = validationRules.find(rule => rule.validationRuleId === ruleId);
      if (originalRule) {
        setEditingRules(prev => 
          prev.map(rule => rule.validationRuleId === ruleId ? { ...originalRule } : rule)
        );
      }
    }
  };

  const updateRule = (ruleId: number, updates: Partial<ValidationRuleDto>) => {
    // Handle special case for cash movement rule type auto-population
    if (updates.ruleType === "unexplained-cash-movement") {
      const template = RuleTemplates["unexplained-cash-movement"];
      const currentRule = editingRules.find(rule => rule.validationRuleId === ruleId);
      
      // Only auto-populate if the rule name is empty (new rule)
      if (currentRule && (!currentRule.ruleName || currentRule.ruleName === "")) {
        updates = {
          ...updates,
          ruleName: template.defaultName,
          shortDescriptionTemplate: template.defaultShortDescription,
          descriptionTemplate: template.defaultDescription,
          scope: template.defaultScope,
          exceptionRule: template.defaultExceptionRule
        };
      }
    }

    setEditingRules(prev => 
      prev.map(rule => 
        rule.validationRuleId === ruleId 
          ? { ...rule, ...updates }
          : rule
      )
    );
    
    // Also update draft rules if it's a draft
    setDraftRules(prev =>
      prev.map(rule =>
        rule.validationRuleId === ruleId ? { ...rule, ...updates } : rule
      )
    );
  };

  const toggleRuleDisabled = async (ruleId: number) => {
    // Update the rule immediately
    setEditingRules(prev =>
      prev.map(rule =>
        rule.validationRuleId === ruleId ? { ...rule, disabled: !rule.disabled } : rule
      )
    );

    // If it's not a draft rule, save it immediately
    const isDraft = draftRules.some(rule => rule.validationRuleId === ruleId);
    if (!isDraft) {
      await saveRule(ruleId);
    }
  };

  const saveRule = async (ruleId: number) => {
    if (!selectedFund?.fundId) return;

    const ruleToSave = editingRules.find(rule => rule.validationRuleId === ruleId);
    if (!ruleToSave) return;

    setIsSaving(true);
    setError(null);

    try {
      // Enhanced logging for data corruption analysis
      if (ruleToSave.accounts && typeof ruleToSave.accounts === 'string' && 
          (ruleToSave.accounts.includes('object Object') || ruleToSave.accounts.includes('[[['))) {
        logDataCorruption(ruleToSave, 'accounts', ruleToSave.accounts, 'WILL_BE_CLEANED');
      }
      if (ruleToSave.excludeAccounts && typeof ruleToSave.excludeAccounts === 'string' && 
          (ruleToSave.excludeAccounts.includes('object Object') || ruleToSave.excludeAccounts.includes('[[['))) {
        logDataCorruption(ruleToSave, 'excludeAccounts', ruleToSave.excludeAccounts, 'WILL_BE_CLEANED');
      }

      // Format accounts field: empty -> "{}", values -> "[123,312]"
      if (ruleToSave.accounts) {
        if (Array.isArray(ruleToSave.accounts)) {
          // Filter out any invalid array elements
          const validAccountIds = ruleToSave.accounts.filter(id => 
            id && 
            typeof id === 'string' && 
            !id.includes('object Object') && 
            id !== '{}' &&
            !id.startsWith('[')
          );
          ruleToSave.accounts = validAccountIds.length === 0 ? "{}" : `[${validAccountIds.join(',')}]`;
        } else if (typeof ruleToSave.accounts === 'string') {
          let cleanString = ruleToSave.accounts.trim();
          
          if (cleanString === '' || cleanString === '{}' || cleanString === '[]') {
            ruleToSave.accounts = "{}";
          } else {
            // Clean up corrupted data with nested brackets and [object Object]
            cleanString = cleanString.replace(/\[+/g, '').replace(/\]+/g, '');
            
            const accountIds = cleanString.split(',').map(id => id.trim()).filter(id => {
              return id && 
                     id !== '{}' && 
                     id !== '[object Object]' && 
                     !id.includes('object Object') &&
                     !id.startsWith('[') &&
                     !id.endsWith(']');
            });
            
            ruleToSave.accounts = accountIds.length > 0 ? `[${accountIds.join(',')}]` : "{}";
          }
        }
      } else {
        ruleToSave.accounts = "{}";
      }

      // Format excludeAccounts field: empty -> "{}", values -> "[123,312]"
      if (ruleToSave.excludeAccounts) {
        if (Array.isArray(ruleToSave.excludeAccounts)) {
          // Filter out any invalid array elements
          const validAccountIds = ruleToSave.excludeAccounts.filter(id => 
            id && 
            typeof id === 'string' && 
            !id.includes('object Object') && 
            id !== '{}' &&
            !id.startsWith('[')
          );
          ruleToSave.excludeAccounts = validAccountIds.length === 0 ? "{}" : `[${validAccountIds.join(',')}]`;
        } else if (typeof ruleToSave.excludeAccounts === 'string') {
          let cleanString = ruleToSave.excludeAccounts.trim();
          
          if (cleanString === '' || cleanString === '{}' || cleanString === '[]') {
            ruleToSave.excludeAccounts = "{}";
          } else {
            // Clean up corrupted data with nested brackets and [object Object]
            cleanString = cleanString.replace(/\[+/g, '').replace(/\]+/g, '');
            
            const accountIds = cleanString.split(',').map(id => id.trim()).filter(id => {
              return id && 
                     id !== '{}' && 
                     id !== '[object Object]' && 
                     !id.includes('object Object') &&
                     !id.startsWith('[') &&
                     !id.endsWith(']');
            });
            
            ruleToSave.excludeAccounts = accountIds.length > 0 ? `[${accountIds.join(',')}]` : "{}";
          }
        }
      } else {
        ruleToSave.excludeAccounts = "{}";
      }

      // Ensure we have the required clientId
      const validationRule: ValidationRuleDto = {
        ...ruleToSave,
        clientId: ruleToSave.clientId || 1, // Default to 1 if not set - should be dynamic based on user's client
        fundId: selectedFund.fundId,
      };

      const savedRule = await createValidationRuleNew(validationRule);
      
      if (savedRule) {
        const isDraft = draftRules.some(rule => rule.validationRuleId === ruleId);
        
        if (isDraft) {
          // Remove from draft rules and add to real rules
          setDraftRules(prev => prev.filter(rule => rule.validationRuleId !== ruleId));
          setValidationRules(prev => [...prev, savedRule]);
          setEditingRules(prev => 
            prev.map(rule => rule.validationRuleId === ruleId ? savedRule : rule)
          );
        } else {
          // Update existing rule
          setValidationRules(prev =>
            prev.map(rule => rule.validationRuleId === ruleId ? savedRule : rule)
          );
          setEditingRules(prev => 
            prev.map(rule => rule.validationRuleId === ruleId ? savedRule : rule)
          );
        }
        
        setEditingStates(prev => ({ ...prev, [ruleId]: false }));
      }
    } catch (err) {
      console.error("Error saving validation rule:", err);
      setError("Failed to save validation rule. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const createDraftRule = () => {
    if (!selectedFund?.fundId) return;

    // Generate a temporary negative ID for draft rules
    const tempId = -Math.floor(Math.random() * 1000000);
    
    const newDraftRule: ValidationRuleDto = {
      validationRuleId: tempId,
      clientId: 1, // Should be dynamic based on user's client
      fundId: selectedFund.fundId,
      ruleName: "",
      ruleType: "",
      accounts: null,
      excludeAccounts: null,
      exceptionRule: null,
      scope: "fund",
      shortDescriptionTemplate: "",
      descriptionTemplate: "",
      disabled: false,
    };

    // Add to draft rules and editing rules
    setDraftRules(prev => [...prev, newDraftRule]);
    setEditingRules(prev => [...prev, newDraftRule]);
    
    // Start editing the new rule immediately
    setEditingStates(prev => ({ ...prev, [tempId]: true }));
  };

  const validateFundRules = async () => {
    if (!selectedFund?.fundId) return;

    setIsValidating(true);
    setError(null);

    try {
      const result = await validateRules(selectedFund.fundId);
      
      if (result) {
        toast.success("Validation completed successfully", {
          description: "All validation rules have been processed.",
        });
      } else {
        toast.error("Validation failed", {
          description: "The validation process encountered issues. Please check your rules and try again.",
        });
      }
      
      console.log("Validation result:", result);
    } catch (err) {
      console.error("Error validating rules:", err);
      setError("Failed to validate rules. Please try again.");
      toast.error("Validation error", {
        description: "Failed to validate rules. Please try again.",
      });
    } finally {
      setIsValidating(false);
    }
  };

  const hasUnsavedChanges = () => {
    return JSON.stringify(validationRules) !== JSON.stringify(editingRules.filter(rule => !draftRules.some(draft => draft.validationRuleId === rule.validationRuleId)));
  };

  const isDraftRule = (ruleId: number) => {
    return draftRules.some(rule => rule.validationRuleId === ruleId);
  };

  useEffect(() => {
    fetchValidationRules();
    fetchSupportedRuleTypes();
  }, [selectedFund?.fundId]);

  return {
    validationRules: editingRules,
    originalRules: validationRules,
    draftRules,
    supportedRuleTypes,
    editingStates,
    isLoading,
    isSaving,
    isValidating,
    error,
    refreshRules,
    startEditing,
    cancelEditing,
    updateRule,
    toggleRuleDisabled,
    saveRule,
    createDraftRule,
    validateFundRules,
    hasUnsavedChanges,
    isDraftRule,
  };
}; 